// 🔴 « Reprendre les travaux de la visite » — décision CTO (lot C, 30/09).
//
// Le relevé chantier CLOS d'un dossier PROPOSE des lignes de devis ; l'humain
// CONFIRME. ⛔ Aucun automatisme : ⛔ pas de devis créé, ⛔ pas de statut touché.
// 🔑 Règles :
//   · un poste est REPRIS seulement si sa `reference_referentiel` désigne un
//     produit ACTIF du catalogue du tenant (liste `/catalogue/produits`, qui ne
//     rend que les actifs non archivés, scopée au tenant par la session) ;
//   · le PRIX vient du CATALOGUE (la route d'ajout de ligne prend le snapshot
//     catalogue) — ⛔ jamais de la visite, qui n'en porte d'ailleurs aucun ;
//   · les postes NON repris sont LISTÉS, avec leur motif — ⛔ rien ne disparaît
//     en silence.
// ⛔ Aucune route nouvelle : lecture `GET /api/chantier/sessions…`, écriture par la
// route EXISTANTE `POST /api/metier/devis/{id}/lignes`.

export const ETAT_RELEVE_CLOS = "clos";

export const MOTIF_SANS_REFERENCE = "sans référence au catalogue";
export const MOTIF_PRODUIT_INACTIF = "référence absente du catalogue actif (produit inactif, archivé ou inconnu)";
export const MOTIF_SANS_QUANTITE = "quantité non relevée";

// Le relevé CLOS le plus récent (par `clos_le`), ou null.
export function releveClosLePlusRecent(releves) {
  const clos = (releves || []).filter((r) => r && r.etat === ETAT_RELEVE_CLOS);
  if (!clos.length) return null;
  return [...clos].sort((a, b) => String(b.clos_le || "").localeCompare(String(a.clos_le || "")))[0];
}

// Postes du relevé ⇒ { repris: [{ poste, produit }], nonRepris: [{ poste, motif }] }.
export function proposerPostes(postes, produitsActifs) {
  const parReference = new Map();
  for (const p of produitsActifs || []) {
    if (!p || p.actif === false || p.archive === true) continue;   // défense en profondeur
    const ref = String(p.reference || "").trim();
    if (ref && !parReference.has(ref)) parReference.set(ref, p);
  }
  const repris = [];
  const nonRepris = [];
  for (const poste of postes || []) {
    const ref = String(poste?.reference_referentiel || "").trim();
    const quantite = Number(poste?.quantite);
    if (!ref) {
      nonRepris.push({ poste, motif: MOTIF_SANS_REFERENCE });
    } else if (!parReference.has(ref)) {
      nonRepris.push({ poste, motif: MOTIF_PRODUIT_INACTIF });
    } else if (!(Number.isFinite(quantite) && quantite > 0)) {
      // ⛔ Une quantité absente n'est PAS remplacée par 1 : ce serait l'inventer.
      nonRepris.push({ poste, motif: MOTIF_SANS_QUANTITE });
    } else {
      repris.push({ poste, produit: parReference.get(ref) });
    }
  }
  return { repris, nonRepris };
}

async function lireJson(fetchFn, url, entetes) {
  const res = await fetchFn(url, { headers: entetes, credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status} sur ${url}`);
  return res.json();
}

// Lit le relevé clos du dossier et le catalogue actif ⇒ proposition (ou null si
// aucun relevé clos). LECTURE SEULE.
export async function chargerProposition({ api, dossierId, entetes, fetchFn = fetch }) {
  const { releves } = await lireJson(
    fetchFn, `${api}/api/chantier/sessions?dossier_id=${encodeURIComponent(dossierId)}`, entetes);
  const clos = releveClosLePlusRecent(releves);
  if (!clos) return null;
  const releve = await lireJson(fetchFn, `${api}/api/chantier/sessions/${encodeURIComponent(clos.id)}`, entetes);
  const produits = await lireJson(fetchFn, `${api}/api/metier/catalogue/produits?limit=500`, entetes);
  return { releve, ...proposerPostes(releve.postes, produits) };
}

// APRÈS CONFIRMATION : ajoute les postes repris, un par un, par la route EXISTANTE.
// Un refus serveur (ex. produit archivé entre-temps ⇒ 400) n'arrête pas les autres :
// le poste rejoint les non repris, avec le motif DONNÉ par le serveur.
export async function appliquerReprise({ api, devisId, entetesJson, repris, extraLigne = {}, fetchFn = fetch }) {
  let devis = null;
  const ajoutes = [];
  const refuses = [];
  for (const { poste, produit } of repris) {
    const res = await fetchFn(`${api}/api/metier/devis/${encodeURIComponent(devisId)}/lignes`, {
      method: "POST",
      headers: entetesJson,
      credentials: "include",
      body: JSON.stringify({ produit_id: produit.id, quantite: Number(poste.quantite), ...extraLigne }),
    });
    const corps = await res.json().catch(() => ({}));
    if (res.ok) {
      devis = corps.devis || devis;
      ajoutes.push({ poste, produit });
    } else {
      refuses.push({ poste, motif: corps?.detail || `refus du serveur (HTTP ${res.status})` });
    }
  }
  return { devis, ajoutes, refuses };
}
